<script setup lang="ts">
import {
  PhDeviceMobile,
  PhHouse,
  PhMagnifyingGlass,
  PhMonitor,
  PhMoon,
  PhShareNetwork,
  PhSun,
} from '@phosphor-icons/vue'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import type { RouteMap } from 'vue-router'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useAppDrawer } from '@/composables/useAppDrawer'
import { useInstallPrompt } from '@/composables/useInstallPrompt'
import { useTheme } from '@/composables/useTheme'

const { open, closeDrawer } = useAppDrawer()
const { canInstall, showIosHint, install } = useInstallPrompt()
const router = useRouter()
const { preference: themePreference, cycleTheme } = useTheme()

const themeLabel = computed(
  () =>
    ({
      auto: 'Theme: following system',
      light: 'Theme: light',
      dark: 'Theme: dark',
    })[themePreference.value],
)

/**
 * `docs/menu.jpg` also lists News, Monthly ticket, Buy stamp, Search offline,
 * Feedback and Helps, plus a profile block and a hotline. Those are omitted
 * rather than rendered disabled: the upstream service does expose endpoints for
 * several of them, but none are in scope here, and a greyed-out row still
 * promises a feature. See the parent PRD's scope decisions.
 */
const ENTRIES = [
  { name: 'track', label: 'Home', icon: PhHouse },
  { name: 'lookup-routes', label: 'Look-up', icon: PhMagnifyingGlass },
] as const

// Close on any successful navigation, including browser back while the drawer
// is open — otherwise the overlay outlives the screen it was opened from.
router.afterEach(() => closeDrawer())

// `keyof RouteMap`, not `string`: this used to accept any string, so a typo in
// ENTRIES compiled and then fell through the catch-all to /track — a dead menu item
// that looked like it worked.
function go(name: keyof RouteMap) {
  router.push({ name })
}
</script>

<template>
  <Sheet v-model:open="open">
    <SheetContent side="left" class="w-[min(80%,18rem)] p-0">
      <SheetHeader class="border-b p-4">
        <SheetTitle class="text-base font-bold">Hanoi Bus</SheetTitle>
        <SheetDescription class="text-xs text-muted-foreground">
          Network explorer
        </SheetDescription>
      </SheetHeader>

      <nav class="flex flex-col p-2" aria-label="Main">
        <button
          v-for="entry in ENTRIES"
          :key="entry.name"
          type="button"
          class="flex min-h-12 items-center gap-3 px-3 py-3 text-left text-sm transition-colors hover:bg-muted"
          :class="
            router.currentRoute.value.name === entry.name
              ? 'font-bold text-foreground'
              : 'text-body'
          "
          @click="go(entry.name)"
        >
          <component :is="entry.icon" class="size-5 shrink-0" />
          {{ entry.label }}
        </button>
      </nav>

      <div class="mt-auto flex flex-col gap-2 border-t p-3">
        <!-- Only one of these can ever render, and often neither: Chromium gets
             a real button, iOS gets an instruction because it exposes no API,
             and an already-installed app gets nothing at all. Telling someone to
             install an app they are already running is its own kind of broken. -->
        <Button
          v-if="canInstall"
          variant="outline"
          type="button"
          class="w-full justify-start gap-3"
          @click="install"
        >
          <PhDeviceMobile />
          Install app
        </Button>

        <p
          v-else-if="showIosHint"
          class="flex items-center gap-3 px-3 py-2 text-xs text-muted-foreground"
        >
          <PhShareNetwork class="size-5 shrink-0" />
          <span>To install: Share → Add to Home Screen</span>
        </p>

        <Button
          variant="outline"
          type="button"
          class="w-full justify-start gap-3"
          :aria-label="themeLabel"
          @click="cycleTheme"
        >
          <PhMonitor v-if="themePreference === 'auto'" />
          <PhSun v-else-if="themePreference === 'light'" />
          <PhMoon v-else />
          {{ themeLabel }}
        </Button>
      </div>
    </SheetContent>
  </Sheet>
</template>
